import { useEffect, useRef, useState } from 'react';
import { Replayer } from '@rrweb/replay';
import '@rrweb/replay/dist/style.css';
import type { eventWithTime } from '@rrweb/types';
import type { RecordingChunksDto } from '@daevio/shared';
import { RecordingSanitizer } from '../lib/sanitizeRecording.ts';

/** Loads the recording chunks stored after a seq (`-1`: from the start). */
export type LiveLoader = (after: number) => Promise<RecordingChunksDto>;

const POLL_MS = 2000;
/**
 * How far behind the recorder the live view plays. The recorder sends a batch every 5 s and we ask every
 * 2 s: with this margin the events are usually here before their turn, so the page moves smoothly.
 */
const LIVE_DELAY_MS = 7000;
/** Chunks are numbered by the recorder and may land out of order: each request looks back this many. */
const LOOK_BACK = 3;

/**
 * The visit as it happens: an rrweb replayer in live mode, fed with the chunks the recorder has stored so
 * far. Shows `placeholder` until the first full snapshot arrives. The rebuilt page is scaled to fit the
 * element (`contain`) or its width with the height of a 16:10 stage (`width`).
 */
export function LiveStage({
  load,
  fit,
  color = '#D6246E',
  className,
  placeholder,
  onPlayhead,
}: {
  load: LiveLoader;
  fit: 'contain' | 'width';
  color?: string;
  className?: string;
  placeholder?: React.ReactNode;
  /** Called every half second with the recorder's time on screen (epoch ms), to sync captions with the view. */
  onPlayhead?: (ts: number) => void;
}) {
  const outerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const loader = useRef(load);
  loader.current = load;
  const playheadRef = useRef(onPlayhead);
  playheadRef.current = onPlayhead;
  const [started, setStarted] = useState(false);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const outer = outerRef.current;
    const root = rootRef.current;
    if (!outer || !root) return;
    let alive = true;
    let timer: number | undefined;
    let clock: number | undefined;
    let replayer: Replayer | null = null;
    let frame: { width: number; height: number } | null = null;
    const seen = new Set<number>();
    let lastSeq = -1;
    let buffered: eventWithTime[] = [];
    const sanitizer = new RecordingSanitizer();
    const add = (e: eventWithTime) => {
      const kept = sanitizer.push(e);
      if (kept) replayer?.addEvent(kept);
    };

    const boxHeight = () => {
      if (fit === 'contain') return outer.clientHeight;
      const maxHeight = Math.max(280, window.innerHeight - 260);
      return Math.min(maxHeight, Math.round((outer.clientWidth * 10) / 16));
    };
    const layout = () => {
      if (fit === 'width') setHeight(boxHeight());
      if (!replayer || !frame) return;
      const scale = Math.min(1, outer.clientWidth / frame.width, boxHeight() / frame.height);
      replayer.wrapper.style.transform = `translate(-50%, -50%) scale(${scale})`;
    };

    const start = () => {
      const events = buffered.sort((a, b) => a.timestamp - b.timestamp);
      buffered = [];
      replayer = new Replayer([], {
        root,
        liveMode: true,
        showWarning: false,
        triggerFocus: false,
        mouseTail: { strokeStyle: color, lineWidth: 2, duration: 700 },
      });
      // The placeholder stays until the first page is drawn (the live view runs a few seconds behind).
      replayer.on('fullsnapshot-rebuilded', () => setStarted(true));
      replayer.on('resize', (d) => {
        frame = d as { width: number; height: number };
        layout();
      });
      // Joins a few seconds behind the latest event: what came before is applied at once.
      const baseline = events[events.length - 1]!.timestamp - LIVE_DELAY_MS;
      const startedAt = Date.now();
      replayer.startLive(baseline);
      clock = window.setInterval(() => playheadRef.current?.(baseline + Date.now() - startedAt), 500);
      for (const e of events) add(e);
    };

    const poll = async () => {
      let more = false;
      try {
        const res = await loader.current(Math.max(-1, lastSeq - LOOK_BACK));
        if (!alive) return;
        more = res.more;
        const fresh: eventWithTime[] = [];
        for (const chunk of res.chunks) {
          if (seen.has(chunk.seq)) continue;
          seen.add(chunk.seq);
          lastSeq = Math.max(lastSeq, chunk.seq);
          fresh.push(...(chunk.events as eventWithTime[]));
        }
        if (replayer) {
          for (const e of fresh.sort((a, b) => a.timestamp - b.timestamp)) add(e);
        } else {
          buffered.push(...fresh);
          // A late joiner first catches up with what is stored, then starts from the newest moment.
          if (!more && buffered.some((e) => e.type === 2)) start();
        }
      } catch {
        // A failed poll is retried with the next one.
      }
      if (alive) timer = window.setTimeout(() => void poll(), more ? 0 : POLL_MS);
    };

    layout();
    void poll();
    window.addEventListener('resize', layout);
    return () => {
      alive = false;
      window.clearTimeout(timer);
      window.clearInterval(clock);
      window.removeEventListener('resize', layout);
      replayer?.destroy();
      root.innerHTML = '';
    };
  }, [fit, color]);

  return (
    <div ref={outerRef} className={`live-stage live-stage-${fit}${className ? ` ${className}` : ''}`} style={{ height }} data-testid="live-stage">
      <div ref={rootRef} className="live-stage-root daevio-block" style={started ? undefined : { visibility: 'hidden' }} aria-hidden="true" />
      {!started && placeholder}
    </div>
  );
}
